import { useMemo } from 'react'

import { DateTime } from 'luxon'

import {
  digObject, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useCreditNotes from '@hooks/useCreditNotes'
import useInvoices from '@hooks/useInvoices'

import type {
  CreditNoteRequestOptions, InvoiceRequestOptions, CreditNoteModel, InvoiceModel,
} from '@models/types'

const watchEntityKeys = ['creditNotes', 'invoices']

interface UseAccountingItemsParams {
  filters: {
    agency_id?: number,
    approved?: 'true' | 'false',
    campaign_id?: number,
    creditor_id?: number,
    creditor_type?: 'Agency' | 'Brand' | 'Provider' | 'System',
    debtor_id?: number,
    debtor_type?: 'Agency' | 'Brand' | 'Provider',
    end_date?: string,
    start_date?: string,
    string?: string,
    sync_status?: string,
  },
  requestOptions?: InvoiceRequestOptions | CreditNoteRequestOptions,
  performHttpRequests: boolean,
}

const useAccountingItems = (params: UseAccountingItemsParams) => {
  const { filters, requestOptions, performHttpRequests } = params

  const {
    approved, creditor_type: filterCreditorType, debtor_type, ...creditNotesFilters
  } = filters

  const {
    updatedEntities: { creditNotes: creditNotesUpdatedAt, invoices: invoicesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const invoicesPayload = useInvoices({
    filters,
    performHttpRequests,
    requestOptions,
  })

  const {
    callbacks: { loadMore: loadMoreInvoices },
    canLoadMore: canLoadMoreInvoices,
    filteredInvoices,
    loading: loadingInvoices,
  } = invoicesPayload

  const creditNotesPayload = useCreditNotes({
    filters: creditNotesFilters,
    performHttpRequests,
    requestOptions,
  })

  const {
    callbacks: { loadMore: loadMoreCreditNotes },
    canLoadMore: canLoadMoreCreditNotes,
    filteredCreditNotes,
    loading: loadingCreditNotes,
  } = creditNotesPayload

  const filteredAccountingItems = useMemo(() => {
    const accountingItems: (InvoiceModel | CreditNoteModel)[] = filterCreditorType === 'Agency'
      ? [...filteredInvoices, ...filteredCreditNotes]
      : filteredInvoices

    return sortArrayBy(accountingItems, 'desc', (item: InvoiceModel | CreditNoteModel) => {
      const createdDateString = digObject(item, 'dates.created.date_time')

      return DateTime.fromFormat(createdDateString, 'dd/MM/yyyy HH:mm')
    })
  }, [JSON.stringify(filters), creditNotesUpdatedAt, invoicesUpdatedAt])

  const loading = loadingCreditNotes || loadingInvoices
  const filteredAccountingItemsCount = filteredAccountingItems.length
  const hasFilteredAccountingItems = !!filteredAccountingItemsCount

  return {
    callbacks: {
      loadMore: () => {
        if (canLoadMoreCreditNotes) loadMoreCreditNotes()
        if (canLoadMoreInvoices) loadMoreInvoices()
      },
    },
    canLoadMore: canLoadMoreCreditNotes || canLoadMoreInvoices,
    filteredAccountingItems,
    filteredAccountingItemsCount,
    hasFilteredAccountingItems,
    loading,
  }
}

export default useAccountingItems
